/**
 * Adding a code, as a page.
 *
 * This was a dialog, and a dialog is the wrong container for a form on a device
 * with an on-screen keyboard. A dialog renders through a portal, so it sits
 * outside the screen that knows how much of the viewport the keyboard covers;
 * its backdrop positioning, its height cap and the field being typed into all
 * have to be corrected separately. A page gets that once, from `Screen`.
 *
 * It is also the better shape for the job: two fields, a keyboard covering half
 * the display, and a merchant who may want the back gesture to mean cancel.
 */

import { useCallback, useState } from 'react';
import { ActivityIndicator } from 'react-native';
import { useNavigate } from 'react-router-dom';
import { createTable } from '@yummy/db';
import {
  Button,
  Card,
  ErrorBanner,
  PageHeading,
  Row,
  Screen,
  Stack,
  Text,
  TextField,
  useAction,
  useLocale,
  useTheme,
  useToast,
} from '@yummy/ui-rnr';

import { FooterBar } from '../components/FooterBar';
import { useMerchant } from '../app/auth';
import { supabase } from '../lib/supabase';

/**
 * Renders the create-a-code form.
 *
 * @returns The form page.
 */
export function TableFormScreen() {
  const { t } = useLocale();
  const theme = useTheme();
  const toast = useToast();
  const navigate = useNavigate();
  const { restaurant } = useMerchant();
  const restaurantId = restaurant?.id ?? null;

  const action = useAction();
  const [label, setLabel] = useState('');
  const [seats, setSeats] = useState('');

  const back = useCallback(() => navigate('/tables'), [navigate]);

  const create = useCallback(async () => {
    if (!restaurantId || !label.trim()) return;

    const parsedSeats = seats.trim() ? Number(seats) : null;
    const created = await action.run(() =>
      createTable(supabase, restaurantId, label.trim(), parsedSeats),
    );

    if (created) {
      toast.show(t('merchant.tables.added', { label: created.label }), { tone: 'success' });
      back();
    }
  }, [restaurantId, label, seats, action, toast, t, back]);

  return (
    <Screen
      // Pinned rather than scrolled with the form: a phone in portrait has room
      // for a couple of fields once the keyboard is up, and the action that
      // commits the form should not be the thing that requires a scroll.
      footer={
        <FooterBar>
          <Row gap="sm" justify="end">
            <Button variant="secondary" onPress={back} accessibilityLabel={t('common.cancel')}>
              <Text>{t('common.cancel')}</Text>
            </Button>
            <Button
              onPress={() => void create()}
              disabled={action.pending || !label.trim()}
              accessibilityLabel={t('merchant.tables.create')}
              aria-busy={action.pending}>
              {action.pending ? (
                <ActivityIndicator color={theme.palette.primaryForeground} size="small" />
              ) : (
                <Text>{t('merchant.tables.create')}</Text>
              )}
            </Button>
          </Row>
        </FooterBar>
      }>
      <Stack gap="xl">
        <PageHeading
          title={t('merchant.tables.addTitle')}
          subtitle={t('merchant.tables.addSubtitle')}
        />

        <Card className="p-4">
          <Stack gap="lg">
            <TextField
              label={t('merchant.tables.labelField')}
              required
              value={label}
              onChangeText={setLabel}
              placeholder={t('merchant.tables.labelPlaceholder')}
              autoFocus
              returnKeyType="next"
              onSubmitEditing={() => void create()}
            />
            <TextField
              label={t('merchant.tables.seatsField')}
              value={seats}
              onChangeText={setSeats}
              keyboardType="number-pad"
              placeholder={t('merchant.tables.seatsPlaceholder')}
              hint={t('merchant.tables.seatsHint')}
            />

            {action.error ? (
              <ErrorBanner message={action.error} onRetry={() => void create()} />
            ) : null}
          </Stack>
        </Card>
      </Stack>
    </Screen>
  );
}
